import { ChangeDetectionStrategy, Component, input, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';

import {
  IconSizeEnum, IProductClassification, IProductFeature, PositionEnum,
  SdAccordionComponent, SdAccordionGroupComponent, SdFootnoteComponent,
  SdTitleComponent, ThemesTextEnum
} from '@seb/web-design-system/core';

import { ITableFeatures } from './table-features.interface';
import { SdTableComponent } from '../table/table.component';

@Component({
  selector: 'sd-table-features',
  templateUrl: 'table-features.component.html',
  styleUrls: ['./table-features.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgClass,
    NgTemplateOutlet,
    SdTitleComponent,
    SdAccordionGroupComponent,
    SdAccordionComponent,
    SdFootnoteComponent,
    SdTableComponent
  ]
})
export class SdTableFeaturesComponent implements OnChanges {
  isOpen = input(true);
  features = input<ITableFeatures>();
  isLoading = input(false);

  customClassSubtitle = 'is-body-s';
  iconSize = IconSizeEnum.small;
  positionEnum = PositionEnum;
  themesTextEnum = ThemesTextEnum;

  allData: {
    maxLine: number;
    code: string;
    name: string;
    entries: any;
    columns: any[];
  }[] = [];

  ngOnChanges(changes: SimpleChanges) {
    const featuresChange = changes['features'];

    if (featuresChange && featuresChange.previousValue !== featuresChange.currentValue) {
      this.allData = this.features()?.classifications.map((classification: IProductClassification) => {
        const classificationFeatures = classification?.features;
        return {
          maxLine: classificationFeatures?.length + 1,
          code: classification?.code,
          name: classification?.name,
          entries: this.getEntries(classificationFeatures),
          columns: this.getColumn(classificationFeatures)
        };
      });
    }
  }

  private getEntries(features: IProductFeature[]) {
    const formatFeatureValue = (values, unit) => {
      if (!values || !values.length) return '';
      const value = values[0]?.value ?? '';
      return unit?.symbol ? `${value} ${unit.symbol}` : value;
    };

    return features.map((obj) => {
      const newObj = {
        description: {
          label: obj.description
        }
      };

      const { featureValues, featureUnit } = obj;
      const formattedValue = formatFeatureValue(featureValues, featureUnit);

      if (featureValues?.length > 1) {
        featureValues.forEach((value, index) => {
          newObj[`featureValues${index}`] = {
            label: formatFeatureValue([value], featureUnit)
          };
        });
      } else {
        newObj['featureValues'] = {
          label: formattedValue
        };
      }

      return newObj;
    });
  }

  private getColumn(features: IProductFeature[]) {
    const featuresColumns = [];
    const { columnsSize } = this.features();

    Object.keys(features[0]).forEach((key) => {
      if (key === 'featureValues' && features[0][key]?.length > 1) {
        features[0][key].forEach((_, i) => {
          featuresColumns.push({
            id: `${key}${i}`
          });
        });
      } else if (key !== 'featureUnit') {
        featuresColumns.push({
          id: key
        });
      }
    });

    featuresColumns.forEach((obj, index) => {
      obj.width = columnsSize ? columnsSize[index] : 1;
    });

    return featuresColumns;
  }
}
